import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { Logo } from './Logo'
import { cn } from '../lib/cn'

interface Props {
  onSizer: () => void
  onTalk: () => void
}

export function Header({ onSizer, onTalk }: Props) {
  const [scrolled, setScrolled] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 64)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const variant = scrolled ? 'light' : 'dark'
  const navTextColor = scrolled ? 'text-navy hover:text-navy/70' : 'text-white/85 hover:text-mint'
  const dividerColor = scrolled ? 'bg-mint' : 'bg-mint'
  const wordmarkColor = scrolled ? 'text-navy' : 'text-white'

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-40 transition-all duration-200',
        scrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-mist'
          : 'bg-transparent border-b border-transparent'
      )}
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="h-16 flex items-center justify-between gap-4">
          <a href="#top" className="inline-flex items-center gap-3 group" aria-label="CyLogic — CyCloud home">
            <Logo height={32} variant={variant} showWordmark />
            <span className={cn('w-px h-6', dividerColor)} aria-hidden="true" />
            <span
              className={cn(
                'font-medium tracking-tight transition-colors',
                wordmarkColor
              )}
              style={{ fontFamily: 'Lexend, system-ui, sans-serif', fontSize: '18px' }}
            >
              CyCloud
            </span>
          </a>

          <nav className="hidden sm:flex items-center gap-7">
            <button
              onClick={onSizer}
              className={cn('text-sm font-medium transition-colors', navTextColor)}
            >
              Sizer
            </button>
            <button
              onClick={onTalk}
              className={cn('text-sm font-medium transition-colors', navTextColor)}
            >
              Talk to us
            </button>
          </nav>

          <button
            onClick={() => setDrawerOpen(true)}
            className={cn('sm:hidden p-2 -mr-2 rounded transition-colors', navTextColor)}
            aria-label="Open menu"
          >
            <Menu size={22} />
          </button>
        </div>
      </div>

      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm sm:hidden"
          onClick={() => setDrawerOpen(false)}
        >
          <div
            className="absolute top-0 right-0 bottom-0 w-72 max-w-[85vw] bg-white shadow-card-hover p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-8">
              <Logo height={28} variant="light" showWordmark />
              <button
                onClick={() => setDrawerOpen(false)}
                className="p-2 -mr-2 text-navy/60 hover:text-navy"
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex flex-col gap-1">
              <button
                onClick={() => {
                  setDrawerOpen(false)
                  onSizer()
                }}
                className="text-left px-4 py-3 rounded text-navy font-medium hover:bg-mist"
              >
                Sizer
              </button>
              <button
                onClick={() => {
                  setDrawerOpen(false)
                  onTalk()
                }}
                className="text-left px-4 py-3 rounded text-navy font-medium hover:bg-mist"
              >
                Talk to us
              </button>
            </nav>
          </div>
        </div>
      )}
    </header>
  )
}
